import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Loader2, ChevronRight, Infinity } from 'lucide-react';

const TrainingCardsSection = ({ limit, showHeader = true }) => {
  const [trainings, setTrainings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hoveredId, setHoveredId] = useState(null);
  const navigate = useNavigate();
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => { fetchTrainings(); }, []);

  const fetchTrainings = async () => {
    try {
      const res = await fetch(`${API_URL}/api/public/trainings`);
      if (res.ok) {
        const data = await res.json();
        const eventTrainings = data.filter(t => t.city && t.city !== '');
        setTrainings(limit ? eventTrainings.slice(0, limit) : eventTrainings);
      }
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const handleCardClick = (t) => {
    if (t.is_online && t.redirect_url) navigate(t.redirect_url);
    else navigate(`/egitim/${t.id}`);
  };

  const getTypeLabel = (item) => {
    if (item.training_format) return item.training_format;
    if (item.is_online) return 'ONLINE';
    return 'Y\u00dcZ Y\u00dcZE';
  };

  const isMasterclass = (item) => item.training_format === 'MASTERCLASS';
  const isVideo = (item) => item.training_format === 'VIDEO';

  const formatPrice = (price) => {
    if (!price || price === 0) return null;
    return `${price.toLocaleString('tr-TR')}\u20BA`;
  };

  const getMonthLabel = (dateStr) => {
    if (!dateStr) return '';
    const d = dateStr.toLowerCase();
    if (d.includes('her zaman')) return '\u221e';
    // Extract just the month name - avoid matching 'mart' inside 'cumartesi'
    const months = [
      ['ocak', 'OCA'], ['\u015fubat', '\u015eUB'], ['mart ', 'MAR'],
      ['nisan', 'N\u0130S'], ['may\u0131s', 'MAY'], ['haziran', 'HAZ'],
      ['temmuz', 'TEM'], ['a\u011fustos', 'A\u011eU'], ['eyl\u00fcl', 'EYL'],
      ['ekim', 'EK\u0130'], ['kas\u0131m', 'KAS'], ['aral\u0131k', 'ARA'],
    ];
    for (const [key, label] of months) {
      if (d.includes(key)) return label;
    }
    return '';
  };

  if (loading) {
    return (
      <section className="w-full bg-black py-20 px-6" data-testid="training-cards-loading">
        <div className="max-w-7xl mx-auto flex justify-center">
          <Loader2 className="w-8 h-8 text-purple-400 animate-spin" />
        </div>
      </section>
    );
  }

  if (trainings.length === 0) return null;

  return (
    <section className="w-full bg-black py-20 px-4 sm:px-6" data-testid="training-cards-section">
      <div className="max-w-7xl mx-auto">
        {showHeader && (
          <motion.div className="mb-16" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-semibold text-white leading-tight tracking-tight">
              Hayat&#305;n&#305; de&#287;i&#351;tiren &#351;ey bilgi de&#287;ildir&#8230;
              <br />
              <span className="bg-gradient-to-r from-purple-400 via-pink-400 to-purple-500 bg-clip-text text-transparent">
                Do&#287;ru anda verdi&#287;in karard&#305;r.
              </span>
            </h2>
            <p className="text-white/40 mt-4 text-base md:text-lg font-light">
              A&#351;a&#287;&#305;daki tarihler bir e&#287;itim listesi de&#287;il. Haz&#305;r olanlar&#305;n se&#231;ti&#287;i ge&#231;i&#351; noktalar&#305;.
            </p>
          </motion.div>
        )}

        <div className="flex flex-col gap-5" data-testid="trainings-list">
          {trainings.map((item, i) => {
            const mc = isMasterclass(item);
            const vid = isVideo(item);
            const price = formatPrice(item.price);
            const monthLabel = getMonthLabel(item.date);
            const isHovered = hoveredId === item.id;

            return (
              <motion.div
                key={item.id || i}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ delay: i * 0.06, duration: 0.4 }}
                onClick={() => handleCardClick(item)}
                onMouseEnter={() => setHoveredId(item.id)}
                onMouseLeave={() => setHoveredId(null)}
                className={`group cursor-pointer transition-all duration-500 ${
                  hoveredId && hoveredId !== item.id ? 'blur-[8px] opacity-[0.04] scale-[0.98]' : ''
                }`}
                data-testid={`training-card-${item.city?.toLowerCase() || i}`}
              >
                <div className={`relative flex items-stretch rounded-2xl overflow-hidden transition-all duration-500 ${
                  mc
                    ? 'bg-emerald-500/[0.04] border border-emerald-500/20 hover:border-emerald-400/40 hover:shadow-[0_0_30px_-5px_rgba(16,185,129,0.12)]'
                    : vid
                      ? 'bg-white/[0.015] border border-white/[0.05] border-dashed hover:border-white/[0.12]'
                      : 'bg-white/[0.025] border border-white/[0.07] hover:border-purple-500/25 hover:shadow-[0_0_25px_-5px_rgba(168,85,247,0.08)]'
                }`}>

                  {/* Left accent bar with month label */}
                  <div className={`w-10 flex-shrink-0 flex flex-col items-center justify-center transition-all duration-500 ${
                    mc
                      ? 'bg-gradient-to-b from-emerald-500/20 to-emerald-600/10 group-hover:from-emerald-500/30 group-hover:to-emerald-600/15'
                      : vid
                        ? 'bg-white/[0.02] group-hover:bg-white/[0.04]'
                        : 'bg-gradient-to-b from-purple-500/10 via-fuchsia-500/10 to-pink-500/10 group-hover:from-purple-500/15 group-hover:to-pink-500/15'
                  }`}>
                    {vid ? (
                      <Infinity className="w-4 h-4 text-white/25 group-hover:text-white/40 transition-colors duration-300" />
                    ) : (
                      <span className={`text-[10px] font-bold tracking-widest ${
                        mc ? 'text-emerald-400/70' : 'text-purple-400/50'
                      }`}>
                        {monthLabel}
                      </span>
                    )}
                  </div>

                  {/* Hover glow */}
                  <div className={`absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none ${
                    mc ? 'bg-gradient-to-r from-emerald-500/[0.04] to-transparent' : vid ? '' : 'bg-gradient-to-r from-purple-500/[0.03] to-transparent'
                  }`} />

                  {/* === DESKTOP === */}
                  <div className={`relative flex-1 hidden md:grid md:grid-cols-[130px_1fr_110px_200px_120px_180px] items-center px-5 gap-4 ${
                    mc ? 'py-7' : 'py-5'
                  }`}>

                    {/* Type Badge */}
                    <div className="overflow-hidden">
                      <span className={`inline-block px-3 py-1.5 rounded-md text-[11px] font-bold tracking-wider uppercase whitespace-nowrap ${
                        mc
                          ? 'bg-emerald-500/15 text-emerald-300 border border-emerald-500/20'
                          : vid
                            ? 'bg-white/[0.05] text-white/40 border border-white/[0.08]'
                            : item.is_online || (item.training_type || '').toLowerCase() === 'online'
                              ? 'bg-white/[0.08] text-white/80 border border-white/[0.1]'
                              : 'bg-purple-500/[0.12] text-purple-300 border border-purple-500/[0.15]'
                      }`}>
                        {getTypeLabel(item)}
                      </span>
                    </div>

                    {/* Title + Badge */}
                    <div className="min-w-0 pr-2">
                      <h3 className={`font-semibold transition-colors duration-300 truncate ${
                        mc ? 'text-xl text-emerald-100 group-hover:text-emerald-50' : 'text-lg text-white/90 group-hover:text-white'
                      }`}>
                        {item.title}
                      </h3>
                      <div className="flex items-center gap-2 mt-1">
                        {item.badge && (
                          <span className={`px-2 py-0.5 rounded text-[10px] font-bold tracking-wider uppercase ${
                            mc
                              ? 'bg-emerald-500/20 text-emerald-300'
                              : item.badge.includes('YEN')
                                ? 'bg-blue-500/20 text-blue-300'
                                : 'bg-red-500/15 text-red-300/80'
                          }`}>
                            {item.badge}
                          </span>
                        )}
                      </div>
                      <AnimatePresence>
                        {isHovered && item.features && item.features.length > 0 && (
                          <motion.p
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                            transition={{ duration: 0.2 }}
                            className="text-xs text-white/30 mt-1.5 truncate"
                          >
                            {item.features.slice(0, 3).join(' \u2022 ')}
                          </motion.p>
                        )}
                      </AnimatePresence>
                    </div>

                    {/* Price */}
                    <div>
                      {price ? (
                        <motion.span
                          animate={isHovered ? { scale: [1, 1.08, 1] } : { scale: 1 }}
                          transition={{ duration: 0.5 }}
                          className={`inline-block text-base font-bold ${mc ? 'text-emerald-300' : 'text-white/80'}`}
                        >
                          {price}
                        </motion.span>
                      ) : (
                        <span className="text-sm text-white/25">&mdash;</span>
                      )}
                    </div>

                    {/* Date */}
                    <div>
                      <p className="text-[15px] text-white/55 font-medium leading-tight">{item.date}</p>
                      {item.time && <p className="text-xs text-white/30 mt-1">{item.time}</p>}
                      {item.status_text && <p className={`text-[11px] mt-1.5 font-medium ${mc ? 'text-emerald-400/60' : 'text-purple-400/50'}`}>{item.status_text}</p>}
                    </div>

                    {/* Location */}
                    <div>
                      <p className="text-[15px] text-white/55">{item.place || item.city}</p>
                    </div>

                    {/* CTA */}
                    <div className="flex justify-end">
                      <div
                        className={`flex items-center gap-1.5 px-5 py-2.5 rounded-full text-sm font-semibold tracking-wide whitespace-nowrap transition-all duration-400 text-center ${
                          mc
                            ? 'bg-emerald-500/15 text-emerald-200 border border-emerald-500/25 group-hover:bg-emerald-500 group-hover:text-black group-hover:border-emerald-500'
                            : vid
                              ? 'bg-white/[0.04] text-white/50 border border-white/[0.08] group-hover:bg-white/[0.1] group-hover:text-white/80'
                              : 'bg-white/[0.06] text-white/80 border border-white/[0.1] group-hover:bg-white group-hover:text-black group-hover:border-white'
                        }`}
                        data-testid={`training-cta-${item.city?.toLowerCase() || i}`}
                      >
                        {item.cta_text || 'HEMEN KATIL'}
                        <ChevronRight className="w-3.5 h-3.5 opacity-0 -ml-1 group-hover:opacity-100 group-hover:ml-0 transition-all duration-300" />
                      </div>
                    </div>
                  </div>

                  {/* === MOBILE === */}
                  <div className={`relative flex-1 md:hidden px-4 space-y-2.5 ${mc ? 'py-5' : 'py-4'}`}>
                    <div className="flex items-center gap-2">
                      <span className={`inline-block px-2.5 py-0.5 rounded text-[10px] font-bold tracking-wider uppercase ${
                        mc ? 'bg-emerald-500/15 text-emerald-300 border border-emerald-500/20'
                          : item.is_online ? 'bg-white/[0.08] text-white/80 border border-white/[0.1]'
                          : 'bg-purple-500/[0.12] text-purple-300 border border-purple-500/[0.15]'
                      }`}>
                        {getTypeLabel(item)}
                      </span>
                      {item.badge && (
                        <span className={`px-2 py-0.5 rounded text-[9px] font-bold tracking-wider uppercase ${
                          mc ? 'bg-emerald-500/20 text-emerald-300' : 'bg-red-500/15 text-red-300/80'
                        }`}>{item.badge}</span>
                      )}
                    </div>
                    <h3 className={`text-base font-semibold ${mc ? 'text-emerald-100' : 'text-white/90'}`}>{item.title}</h3>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-white/45">{item.date}</p>
                        <p className="text-xs text-white/30">{item.place || item.city}{item.time ? ` \u2022 ${item.time}` : ''}</p>
                      </div>
                      <div className="flex items-center gap-3">
                        {price && <span className={`text-sm font-bold ${mc ? 'text-emerald-300' : 'text-white/70'}`}>{price}</span>}
                        <div className={`px-4 py-2 rounded-full text-xs font-semibold ${
                          mc ? 'bg-emerald-500/15 text-emerald-200 border border-emerald-500/25'
                            : 'bg-white/[0.06] text-white/80 border border-white/[0.1]'
                        }`}>
                          {item.cta_text || 'KATIL'}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Footer */}
        <motion.div className="mt-16 text-center" initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ delay: 0.3 }}>
          <p className="text-white/25 font-light">
            Hangi &#351;ehirde olursan ol&#8230; mesele yer de&#287;il. <br />
            <span className="text-white font-medium">Karar.</span>
          </p>
          {limit && trainings.length >= limit && (
            <Link to="/egitimler" className="inline-block mt-6">
              <motion.div className="inline-flex items-center gap-2 px-8 py-3 rounded-full bg-white/[0.04] border border-white/[0.08] text-white/70 hover:bg-purple-500/10 hover:border-purple-500/20 hover:text-white transition-all duration-400 text-sm font-medium" whileHover={{ y: -2 }} data-testid="view-all-trainings-btn">
                T&#252;m E&#287;itimleri G&#246;r
              </motion.div>
            </Link>
          )}
        </motion.div>
      </div>
    </section>
  );
};

export default TrainingCardsSection;
